import { FC, JSX, useMemo } from 'react';
import _sortBy from 'lodash/sortBy';
import { ECommentRichContentCategory, IMentionUserRichContent, TArticleDetail, TComment } from 'shared/models';
import { Link } from '@remix-run/react';
import useReflowClick from '~/utils/use-reflow-click';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { useSEOLinkBuilder } from '~/utils/build-seo-url';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { useActiveToCurrentPage } from '~/utils/active/use-active-to-current-page';

import './style.scss';

interface ICommentContentProps {
  comment: TComment;
  positionPrefix?: ClickPositionPrefix;
  // for experience page post modal
  currentArticle?: TArticleDetail;
}

export const CommentContent: FC<ICommentContentProps> = (props) => {
  const { comment, currentArticle, positionPrefix = ClickPositionPrefix.none } = props;
  const { richContent, text } = comment;
  const buildSEOLink = useSEOLinkBuilder();
  const activeToCurrentPage = currentArticle ? useActiveToArticle(currentArticle) : useActiveToCurrentPage();

  const handleCommentRichContentClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentPage([positionPrefix, ClickPosition.comment_mentioned_user]);
      },
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.comment_mentioned_user,
        page_click_id: comment.commentId,
      }),
    },
    [activeToCurrentPage],
  );

  const richContentArray = useMemo<(string | JSX.Element)[]>(() => {
    if (!richContent?.length) {
      return [];
    }

    const sortedRichContent = _sortBy(richContent, '.start');

    let cursor = 0;
    const end = text.length;

    let richContentCursor = 0;
    const richContentEnd = sortedRichContent.length;

    const res: (string | JSX.Element)[] = [];

    while (cursor < end) {
      if (richContentCursor < richContentEnd) {
        res.push(text.substring(cursor, end));
        cursor = end;
        break;
      }

      const currentRich = sortedRichContent[richContentCursor];

      if (cursor === currentRich.start) {
        const { start, length } = currentRich;
        if (currentRich.type === ECommentRichContentCategory.MENTION_USER) {
          const rich = currentRich as IMentionUserRichContent;
          const { mentionUserId } = rich;

          const mentionedUerHref = buildSEOLink(`/user/${mentionUserId}/share`);

          res.push(
            <Link className="comment-mentioned-name" to={mentionedUerHref} onClick={handleCommentRichContentClick}>
              {text.substring(start, start + length)}
            </Link>,
          );
        } else {
          res.push(text.substring(start, start + length));
        }

        richContentCursor += 1;
        cursor += length;
      } else {
        res.push(text.substring(cursor, currentRich.start));
        cursor = currentRich.start;
      }
    }

    return res;
  }, [handleCommentRichContentClick]);

  if (!richContent?.length) {
    return <p className="comment-content">{text}</p>;
  }

  return <p className="comment-content">{richContentArray}</p>;
};
